import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { saveToken, getToken, clearToken, recoverTokenFromIDB, logStorageState } from '../utils/tokenStorage';
import { captureFirstTouch, getFirstTouch, clearFirstTouch } from '../utils/firstTouch';
import TwoFactorModal from '../components/TwoFactorModal';

// Types
interface BrowserInfo {
  browser: string;
  userAgent: string;
  cookiesEnabled: boolean;
  localStorage: boolean;
  thirdPartyCookies: boolean;
  isIOS: boolean;
}

interface User {
  user_id: string;
  name?: string;
  email?: string;
  [key: string]: any;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  pending2FA: boolean;
  register: (name: string, email: string, password: string, accountType?: 'user' | 'trainer') => Promise<any>;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Browser detection helper
const getBrowserInfo = (): BrowserInfo => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  
  return {
    browser,
    userAgent: ua,
    cookiesEnabled: navigator.cookieEnabled,
    localStorage: typeof localStorage !== 'undefined',
    thirdPartyCookies: document.cookie.indexOf('test') !== -1 || true,
    isIOS,
  };
};

// Debug logger
const authDebug = (message: string, data: any = null): void => {
  const timestamp = new Date().toISOString();
  console.log(`[Auth ${timestamp}] ${message}`, data || '');
};

// Track processed session IDs to prevent double-processing (React StrictMode issue)
const processedSessionsMemory = new Set<string>();

const getProcessedSessions = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem('processed_auth_sessions');
    const fromStorage = stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    processedSessionsMemory.forEach(s => fromStorage.add(s));
    return fromStorage;
  } catch {
    return new Set(processedSessionsMemory);
  }
};

const markSessionProcessed = (sessionId: string): void => {
  processedSessionsMemory.add(sessionId);
  
  try {
    const sessions = getProcessedSessions();
    sessions.add(sessionId);
    const arr = Array.from(sessions).slice(-10);
    sessionStorage.setItem('processed_auth_sessions', JSON.stringify(arr));
  } catch {
    // Ignore storage errors
  }
};

const isSessionProcessed = (sessionId: string): boolean => {
  if (processedSessionsMemory.has(sessionId)) return true;
  return getProcessedSessions().has(sessionId);
};

// Helper to map API user response to User type (includes subscription fields)
const mapApiUser = (u: any): User => ({
  user_id: u.user_id || u.id || '',
  name: u.name,
  email: u.email,
  city: u.city,
  country: u.country,
  subscription_status: u.subscription_status,
  subscription_plan: u.subscription_plan,
  subscription_end: u.subscription_end,
  account_type: u.account_type,
  linked_trainer_email: u.linked_trainer_email,
  pending_ai_workout_plan: u.pending_ai_workout_plan,
});

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const processingRef = useRef(false);
  const attributionFiredRef = useRef(false);

  // Universal first-touch attribution — captured ONCE on any page load.
  // Runs before auth so we attribute even if user browses anonymously first.
  useEffect(() => {
    captureFirstTouch();
  }, []);

  // First-touch attribution: when the user becomes authenticated AND we have either
  // a universal `first_touch_source` or the legacy `fatai_first_touch_source` in
  // localStorage, fire the attribution endpoint once. Server only stores on FIRST-touch.
  useEffect(() => {
    if (!user || attributionFiredRef.current) return;
    try {
      const ft = getFirstTouch();
      const legacySrc = localStorage.getItem('fatai_first_touch_source');
      const src = ft?.source || legacySrc;
      if (!src) return;
      attributionFiredRef.current = true;
      const token = getToken();
      if (!token) return;
      // Determine entry point ("via" field): landing / fatai / calculators / other
      let via = 'landing';
      const entry = ft?.entry || '/';
      if (entry === '/fatai' || entry === '/fat-ai' || legacySrc) via = 'fatai';
      else if (entry === '/calculators') via = 'calculators';
      fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/analytics/attribute-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ first_touch_source: src, first_touch_via: via }),
        keepalive: true,
      }).then((r) => { if (r.ok) clearFirstTouch(); }).catch(() => { /* noop */ });
    } catch { /* noop */ }
  }, [user]);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken2FA, setTempToken2FA] = useState('');
  const [emailHint2FA, setEmailHint2FA] = useState('');
  const [isDeviceVerification, setIsDeviceVerification] = useState(false);

  // Initialize device ID for 2FA device tracking
  useEffect(() => {
    if (!localStorage.getItem('device_id')) {
      localStorage.setItem('device_id', 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36));
    }
  }, []);

  // Global error handler for postMessage errors (mobile Google OAuth)
  useEffect(() => {
    const handleError = (event: ErrorEvent): boolean | void => {
      const msg = (event.message || '').toLowerCase();
      if (msg.includes('postmessage') || msg.includes('post message') || msg.includes('failed to execute')) {
        console.log('[Auth] Suppressed postMessage error (mobile OAuth):', event.message);
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    };
    
    // Also handle unhandled promise rejections related to postMessage
    const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      const reason = String(event.reason || '').toLowerCase();
      if (reason.includes('postmessage') || reason.includes('post message') || reason.includes('failed to execute')) {
        console.log('[Auth] Suppressed postMessage rejection:', event.reason);
        event.preventDefault();
      }
    };
    
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Show toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error, { duration: 5000 });
      setTimeout(() => setError(null), 100);
    }
  }, [error]);

  const processGoogleAuth = async (sessionId: string, retryCount = 0): Promise<void> => {
    if (isSessionProcessed(sessionId) || processingRef.current) {
      console.log('[Auth] Session already processed or processing, skipping...', sessionId.substring(0, 10));
      authDebug('Session already processed or processing, skipping...', { sessionId: sessionId.substring(0, 10) });
      // Don't touch loading state — let the active processor handle it
      return;
    }
    
    processingRef.current = true;
    markSessionProcessed(sessionId);
    setLoading(true);
    
    console.log('[Auth] Processing session:', sessionId.substring(0, 10) + '...');
    const browserInfo = getBrowserInfo();
    authDebug('Processing Google auth...', { sessionIdPrefix: sessionId.substring(0, 10), browserInfo, retryCount });
    
    try {
      // Ensure device_id exists before sending session request
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        deviceId = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem('device_id', deviceId);
      }
      const fetchHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      fetchHeaders['X-Device-ID'] = deviceId;
      
      const response = await fetch(`/api/auth/session?session_id=${encodeURIComponent(sessionId)}`, {
        headers: fetchHeaders,
        // Never let a stale browser / PWA / CDN cache feed us a 200+HTML
        // response for this call — always hit the live backend.
        cache: 'no-store',
      });
      if (!response.ok) {
        let errorDetail = `HTTP ${response.status}`;
        try {
          const errData = await response.json();
          errorDetail = errData?.detail || errorDetail;
        } catch { /* ignore parse errors */ }
        throw new Error(errorDetail);
      }
      // Defensive: if the response body is HTML (stale service worker
      // returning index.html, CDN edge cache, etc.), surface a clear,
      // actionable error instead of the confusing "Unexpected token '<'"
      // JSON parse exception. Reading .text() once and parsing manually
      // lets us handle both cases safely.
      const rawBody = await response.text();
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const looksLikeHtml = rawBody.trimStart().startsWith('<');
      if (!contentType.includes('application/json') || looksLikeHtml) {
        authDebug('Google auth: non-JSON body from /api/auth/session', {
          contentType,
          bodyPreview: rawBody.slice(0, 80),
        });
        // Try to self-heal: unregister any stale service worker so the next
        // attempt goes directly to the network.
        try {
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch { /* ignore */ }
        throw new Error('Sign-in response was blocked by an outdated page cache. Please close this tab, reopen the app, and try again.');
      }
      let data: any;
      try {
        data = JSON.parse(rawBody);
      } catch (parseErr: any) {
        authDebug('Google auth: JSON parse failed', { err: parseErr?.message });
        throw new Error('Sign-in response was malformed. Please try again in a moment.');
      }
      
      // Handle 2FA required (also handles device verification)
      if (data.requires_2fa) {
        authDebug('2FA required for Google user', { deviceVerification: data.device_verification });
        setTempToken2FA(data.temp_token || '');
        setEmailHint2FA(data.email_hint || '');
        setIsDeviceVerification(!!data.device_verification);
        setShow2FA(true);
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch { /* noop */ }
        setLoading(false);
        processingRef.current = false;
        return;
      }
      
      console.log('[Auth] Session processed successfully, user:', data.user?.email);
      authDebug('Google auth successful', { userId: data.user?.user_id || data.user?.id, hasToken: !!data.token });
      
      if (data.token) {
        saveToken(data.token);
        authDebug('Token saved to localStorage + cookie');
      }
      // Map the API response to User type
      if (data.user) {
        const mappedUser = mapApiUser(data.user);
        setUser(mappedUser);
        toast.success(`Welcome, ${data.user?.name || 'User'}!`);
      }
      
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (historyErr: any) {
        console.log('[Auth] Could not clean URL:', historyErr.message);
      }
    } catch (err: any) {
      authDebug('Google auth failed', { error: err.message, browserInfo, retryCount });
      
      const errorMsg = err.message || '';
      const isTransientError = errorMsg.includes('520') || 
                               errorMsg.includes('502') || 
                               errorMsg.includes('503') ||
                               errorMsg.includes('504') ||
                               errorMsg.includes('401') ||
                               errorMsg.includes('not found') ||
                               errorMsg.includes('expired') ||
                               errorMsg.includes('timed out') ||
                               errorMsg.includes('network') ||
                               errorMsg.includes('fetch') ||
                               errorMsg.includes('timeout');
      
      if (isTransientError && retryCount < 2) {
        console.log('[Auth] Transient error, retrying...');
        authDebug(`Retrying Google auth (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        processingRef.current = false;
        // Clear BOTH in-memory and sessionStorage so retry isn't blocked
        processedSessionsMemory.delete(sessionId);
        try {
          const sessions = getProcessedSessions();
          sessions.delete(sessionId);
          sessionStorage.setItem('processed_auth_sessions', JSON.stringify(Array.from(sessions)));
        } catch { /* ignore */ }
        return processGoogleAuth(sessionId, retryCount + 1);
      }
      
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (historyErr: any) {
        console.log('[Auth] Could not clean URL on error:', historyErr.message);
      }
      
      console.log('[Auth] Auth failed with error:', errorMsg);
      
      let displayError = '';
      
      if (errorMsg.includes('Invalid state') || errorMsg.includes('invalid_request') || errorMsg.includes('state')) {
        if (browserInfo.browser === 'Firefox') {
          displayError = 'Firefox privacy settings may be blocking sign-in. Try: 1) Click the shield icon in the address bar and disable tracking protection, or 2) Use Chrome/Safari instead.';
        } else {
          displayError = 'Sign-in session expired or invalid. This can happen if you waited too long or refreshed the page. Please try again.';
        }
      } else if (errorMsg.includes('outdated page cache') || errorMsg.includes('response was malformed')) {
        // Stale PWA service-worker / CDN edge cache returned HTML instead of
        // JSON. We already auto-unregister the worker above; surface a
        // specific, actionable message to the user.
        displayError = errorMsg;
      } else if (errorMsg.includes('expired') || errorMsg.includes('not found') || errorMsg.includes('401') || errorMsg.includes('HTTP 401')) {
        displayError = 'Sign-in session expired. Please click "Sign in with Google" again.';
      } else if (errorMsg.includes('520') || errorMsg.includes('502') || errorMsg.includes('503') || errorMsg.includes('504') || errorMsg.includes('timed out')) {
        displayError = 'Server temporarily unavailable. Please try again in a moment.';
      } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
        displayError = 'Network error during sign-in. Please check your connection and try again.';
      } else {
        displayError = `Google sign-in failed: ${errorMsg || 'Unknown error'}. Please try again or use email/password.`;
      }
      
      setError(displayError);
      // Also show toast directly with longer duration — error state may clear quickly during navigation
      toast.error(displayError, { duration: 8000 });
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      authDebug('Checking existing session...');
      logStorageState();
      try {
        let token = getToken();

        // If no token found synchronously, try IndexedDB (async, last resort)
        if (!token) {
          authDebug('No token in sync stores, trying IndexedDB recovery...');
          token = await recoverTokenFromIDB();
        }

        // Auth check: try token first, then fall back to HttpOnly cookie
        if (token) {
          authDebug('Found existing token, validating...');
          try {
            const response = await fetch('/api/auth/me', {
              headers: { 'Authorization': `Bearer ${token}` },
              credentials: 'include'
            });
            if (response.ok && response.status !== 204) {
              const userData = await response.json();
              authDebug('Session valid, user:', userData?.email);
              if (userData) {
                setUser(mapApiUser(userData));
                saveToken(token);
              }
              setLoading(false);
              return; // Auth succeeded, done
            }
            // Token invalid — DON'T clear yet, fall through to cookie auth
            authDebug('Token invalid (HTTP ' + response.status + '), trying cookie fallback...');
          } catch (err: any) {
            authDebug('Token check failed:', err.message);
          }
        }

        // Fallback: try HttpOnly cookie (works even if all JS tokens are wiped).
        // Backend now returns 204 (No Content) when no credentials are present,
        // so anonymous landing-page visitors no longer log a 401 console error
        // (which was costing ~15 Lighthouse Best-Practices points).
        authDebug('Trying HttpOnly cookie auth...');
        try {
          const response = await fetch('/api/auth/me', { credentials: 'include' });
          if (response.status === 204) {
            // Backend explicitly says "no session" — clean exit, no error.
            authDebug('Cookie auth: no session (204)');
            clearToken();
          } else if (response.ok) {
            const userData = await response.json();
            authDebug('Cookie auth SUCCESS, user:', userData?.email);
            if (userData) {
              setUser(mapApiUser(userData));
            }
          } else {
            // Both token AND cookie failed — genuinely not authenticated
            authDebug('Cookie auth also failed — clearing tokens');
            clearToken();
          }
        } catch (err: any) {
          authDebug('Cookie auth error:', err.message);
        }
      } catch (e: any) {
        authDebug('Auth check error:', e.message);
      }
      setLoading(false);
    };

    try {
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId: string | null = null;
      let authError: string | null = null;
      let errorDescription: string | null = null;
      
      console.log('[Auth] === OAuth Callback Check ===');
      console.log('[Auth] Full URL:', window.location.href);
      console.log('[Auth] Origin:', window.location.origin);
      console.log('[Auth] Hash:', hash);
      console.log('[Auth] Search:', search);
      
      authDebug('Checking URL for OAuth callback...', { hash: hash?.substring(0, 50), search: search?.substring(0, 50) });
      
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        sessionId = hashParams.get('session_id');
        authError = hashParams.get('error');
        errorDescription = hashParams.get('error_description');
        console.log('[Auth] From hash - sessionId:', sessionId ? sessionId.substring(0, 10) + '...' : null);
        console.log('[Auth] From hash - error:', authError);
      }
      
      if (!sessionId && !authError && search) {
        const queryParams = new URLSearchParams(search);
        sessionId = queryParams.get('session_id');
        authError = queryParams.get('error');
        errorDescription = queryParams.get('error_description');
        console.log('[Auth] From query - sessionId:', sessionId ? sessionId.substring(0, 10) + '...' : null);
        console.log('[Auth] From query - error:', authError);
      }
      
      // Check for Polar checkout success in query params
      const searchParams = new URLSearchParams(search);
      if ((sessionId && sessionId.startsWith('cs_')) || searchParams.get('polar_checkout_success')) {
        console.log('[Auth] Detected payment checkout redirect, skipping Google auth processing');
        checkSession();
        return;
      }
      
      if (authError) {
        const browserInfo = getBrowserInfo();
        console.log('[Auth] OAuth error detected:', authError, errorDescription);
        authDebug('OAuth error detected', { authError, errorDescription, browserInfo });
        
        let displayError = errorDescription || authError || 'Authentication failed';
        
        if (browserInfo.browser === 'Firefox' && (authError === 'invalid_request' || displayError.includes('state'))) {
          displayError = 'Firefox may be blocking sign-in cookies. Try: Settings → Privacy → disable Enhanced Tracking Protection for this site, or use Chrome/Safari.';
        } else if (displayError.includes('state') || displayError.includes('expired')) {
          displayError = 'Sign-in session expired. Please try again. If this persists, try a different browser.';
        }
        
        setError(displayError);
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }
      
      if (sessionId) {
        authDebug('Found session_id, processing Google auth...', sessionId.substring(0, 10) + '...');
        processGoogleAuth(sessionId);
      } else {
        checkSession();
      }
    } catch (e: any) {
      authDebug('Auth initialization error:', e.message);
      setError('Authentication initialization failed. Please refresh the page.');
      setLoading(false);
    }
   
  }, []);

  const register = useCallback(async (name: string, email: string, password: string, accountType: 'user' | 'trainer' = 'user') => {
    authDebug('Registering user...', { email, accountType });
    setError(null);
    try {
      const data = await api.register(name, email, password, accountType);
      authDebug('Registration successful', { userId: data.user?.user_id || data.user?.id });
      if (data.token) {
        api.setToken(data.token);
        authDebug('Token saved after registration');
      }
      if (data.user) {
        setUser(mapApiUser(data.user));
      }
      return data;
    } catch (err: any) {
      authDebug('Registration failed', { error: err.message });
      
      let displayError = err.message || 'Registration failed';
      if (displayError.includes('already exists') || displayError.includes('duplicate') || displayError.includes('already registered')) {
        displayError = 'An account with this email already exists. Please sign in instead.';
      } else if (displayError.includes('invalid email')) {
        displayError = 'Please enter a valid email address.';
      } else if (displayError.includes('password')) {
        displayError = 'Password must be at least 6 characters long.';
      } else if (displayError.includes('network') || displayError.includes('fetch')) {
        displayError = 'Network error. Please check your connection and try again.';
      } else if (displayError.includes('Server error') || displayError.includes('500')) {
        displayError = 'Server error. Please try again in a moment.';
      }
      
      setError(displayError);
      throw new Error(displayError);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    authDebug('Logging in user...', { email });
    setError(null);
    try {
      const data = await api.login(email, password);
      
      // Handle 2FA required (also handles device verification)
      if (data.requires_2fa) {
        authDebug('2FA required for user', { deviceVerification: data.device_verification });
        setTempToken2FA(data.temp_token || '');
        setEmailHint2FA(data.email_hint || email);
        setIsDeviceVerification(!!data.device_verification);
        setShow2FA(true);
        return data; // Return without setting user
      }
      
      authDebug('Login successful', { userId: data.user?.user_id || data.user?.id });
      if (data.token) {
        api.setToken(data.token);
        authDebug('Token saved after login');
      }
      if (data.user) {
        setUser(mapApiUser(data.user));
      }
      return data;
    } catch (err: any) {
      authDebug('Login failed', { error: err.message });
      
      let displayError = err.message || 'Login failed';
      if (displayError.includes('Invalid') || displayError.includes('incorrect') || displayError.includes('credentials')) {
        displayError = 'Invalid email or password. Please check your credentials and try again.';
      } else if (displayError.includes('not found') || displayError.includes('no user')) {
        displayError = 'No account found with this email. Please sign up first.';
      } else if (displayError.includes('network') || displayError.includes('fetch')) {
        displayError = 'Network error. Please check your connection and try again.';
      }
      
      setError(displayError);
      throw new Error(displayError);
    }
  }, []);

  const loginWithGoogle = useCallback(() => {
    const browserInfo = getBrowserInfo();
    authDebug('Initiating Google login...', browserInfo);
    
    if (browserInfo.browser === 'Firefox') {
      toast.info('Tip: If sign-in fails, try disabling Enhanced Tracking Protection or use Chrome/Safari.', { duration: 5000 });
    }
    
    try {
      const redirectUrl = window.location.origin;
      console.log('[Auth] Google login initiated');
      console.log('[Auth] Current origin:', window.location.origin);
      console.log('[Auth] Redirect URL:', redirectUrl);
      console.log('[Auth] Full auth URL:', `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
      authDebug('Redirecting to auth...', { redirectUrl });
      
      // Save current app state so we can restore after Google redirect
      localStorage.setItem('google_auth_return_app', 'notebook');
      
      // Use location.replace for cleaner navigation (no back button to broken state)
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err: any) {
      console.error('[Auth] Error initiating Google login:', err);
      // Fallback: try with location.replace
      try {
        const redirectUrl = window.location.origin;
        window.location.replace(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
      } catch (fallbackErr) {
        toast.error('Unable to open Google login. Please try again or use email login.');
      }
    }
  }, []);

  const logout = useCallback(async () => {
    authDebug('Logging out...');
    try {
      await api.logout();
      authDebug('Logout successful');
    } catch (err: any) {
      authDebug('Logout error (ignored)', err.message);
    } finally {
      clearToken();
      localStorage.removeItem('walking_active_session');
      setUser(null);
      toast.success('Logged out successfully');
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<User> => {
    try {
      const token = getToken();
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const userData = await response.json();
      if (userData) {
        const mappedUser = mapApiUser(userData);
        setUser(mappedUser);
        return mappedUser;
      }
      throw new Error('No user data returned');
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  }, []);

  // Handle successful 2FA verification
  const handle2FAVerified = useCallback((data: any) => {
    authDebug('2FA verified successfully');
    setShow2FA(false);
    setTempToken2FA('');
    setEmailHint2FA('');
    
    if (data.token) {
      saveToken(data.token);
      api.setToken(data.token);
    }
    if (data.user) {
      const mappedUser = mapApiUser(data.user);
      // Ensure navigation to notebook after 2FA for ALL login types
      // (Google auth sets this before redirect, but email/password login doesn't)
      if (!localStorage.getItem('google_auth_return_app')) {
        localStorage.setItem('google_auth_return_app', 'notebook');
      }
      setUser(mappedUser);
      toast.success(`Welcome, ${data.user?.name || 'User'}!`);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    pending2FA: show2FA,
    register,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <TwoFactorModal
        isOpen={show2FA}
        onClose={() => { setShow2FA(false); setTempToken2FA(''); setEmailHint2FA(''); setIsDeviceVerification(false); }}
        tempToken={tempToken2FA}
        emailHint={emailHint2FA}
        onVerified={handle2FAVerified}
        isDeviceVerification={isDeviceVerification}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
